import React, { useState } from 'react';
import { TableIds } from 'src/api/types/misc';
import { LinkSvg } from 'src/assets/dynamicSvg';
import { NoData, TableSkeleton } from 'src/common';
import ExpandSection from 'src/components/ExpandSection';
import Button from 'src/components/form/Button';
import GenericTable from 'src/components/GenericTable';
import { useStateSelector } from 'src/redux';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import styled from 'styled-components';
import { useScenarioColumns } from './useScenarioColumns';
import { useParams } from 'react-router-dom';
import { LinkScenariosToIncidentModal } from 'src/components/modals';
import { useIncidentLinkedScenariosQuery } from 'src/graphql/_generated';
import { isQueryLoading } from 'src/graphql';

const EndDiv = styled.div`
  .link-icon {
    margin-right: 10px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .link-icon {
      margin-right: 6px;
    }
  }
`;

interface LinkedScenariosSectionProps {
  incidentName: string;
  version?: string;
}

export const LinkedScenariosSection: React.FC<LinkedScenariosSectionProps> = ({ incidentName, version }) => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [isLinkScenarioModalOpen, setIsLinkScenarioModalOpen] = useState(false);
  const colors = useStateSelector(({ theme }) => theme.colors);

  if (isOpen && !hasOpened) setHasOpened(true);

  const { data, loading, networkStatus } = useIncidentLinkedScenariosQuery({
    variables: { id: incidentId!, versionId: version },
    skip: !hasOpened,
  });
  const isLoading = isQueryLoading({ loading, data, networkStatus });

  const scenarios = data?.incident?.scenarios || [];

  const { columns, GTColumnSelectAnchorExported } = useScenarioColumns({
    data: scenarios,
    incidentId: incidentId as string,
  });

  return (
    <>
      {isLinkScenarioModalOpen && (
        <LinkScenariosToIncidentModal
          incidentId={incidentId as string}
          onClose={() => setIsLinkScenarioModalOpen(false)}
          invalidateQueries={['incidentLinkedScenarios']}
          subTitle={{
            title: 'INCIDENT',
            value: incidentName,
          }}
        />
      )}
      <ExpandSection
        title="LINKED SCENARIOS"
        sectionId="incident_linked-scenarios"
        onChange={setIsOpen}
        endComponent={
          isOpen &&
          !version && (
            <EndDiv>
              <Button
                primary
                onClick={() => {
                  setIsLinkScenarioModalOpen(true);
                  mpEvent(MPEvents.ButtonClick, {
                    button: 'Link scenarios',
                    tags: ['INCIDENT', 'SCENARIO'],
                  });
                }}
                data-cy="link-scenarion-btn"
              >
                <LinkSvg className="link-icon" color={colors.btnWhite} />
                LINK SCENARIOS
              </Button>
            </EndDiv>
          )
        }
      >
        {isLoading ? (
          <TableSkeleton />
        ) : scenarios.length ? (
          <GenericTable
            tableId={TableIds.incidentSecnarios}
            data={scenarios}
            columns={columns}
            GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
            searchable
          />
        ) : (
          <NoData title="No scenarios linked" />
        )}
      </ExpandSection>
    </>
  );
};
